import { useEffect, useRef, useState } from "react";
import {
  Play, ClipboardList, Brain, Palette, Database, Code2, Server,
  ShieldCheck, GitPullRequest, FileCheck2, GitMerge, BadgeCheck, Rocket, Check,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { subscribeScroll, prefersReducedMotion } from "@/lib/scrollEngine";

type NodeDef = {
  id: string;
  x: number;
  y: number;
  icon: LucideIcon;
  title: string;
  sub: string;
  run: number;
  done: number;
};

type EdgeDef = { from: string; to: string; a: number; b: number; back?: boolean };

const NODES: NodeDef[] = [
  { id: "request", x: 80, y: 280, icon: Play, title: "Request", sub: "trigger", run: 0, done: 0.06 },
  { id: "po", x: 265, y: 280, icon: ClipboardList, title: "Product Owner", sub: "scope", run: 0.06, done: 0.14 },
  { id: "planner", x: 450, y: 180, icon: Brain, title: "Planner", sub: "plan", run: 0.16, done: 0.26 },
  { id: "designer", x: 450, y: 380, icon: Palette, title: "Designer", sub: "ui · ux", run: 0.18, done: 0.27 },
  { id: "retriever", x: 640, y: 60, icon: Database, title: "Retriever", sub: "context", run: 0.27, done: 0.4 },
  { id: "fe1", x: 640, y: 170, icon: Code2, title: "Coder · FE", sub: "build", run: 0.28, done: 0.42 },
  { id: "fe2", x: 640, y: 280, icon: Code2, title: "Coder · FE", sub: "build", run: 0.29, done: 0.43 },
  { id: "be1", x: 640, y: 390, icon: Server, title: "Coder · BE", sub: "build", run: 0.3, done: 0.44 },
  { id: "be2", x: 640, y: 500, icon: Server, title: "Coder · BE", sub: "build", run: 0.31, done: 0.45 },
  { id: "security", x: 860, y: 140, icon: ShieldCheck, title: "Security", sub: "audit", run: 0.45, done: 0.6 },
  { id: "review", x: 860, y: 280, icon: GitPullRequest, title: "Reviewer", sub: "review", run: 0.46, done: 0.61 },
  { id: "qa", x: 860, y: 420, icon: FileCheck2, title: "Reviewer · QA", sub: "qa", run: 0.47, done: 0.62 },
  { id: "deploy", x: 1055, y: 280, icon: GitMerge, title: "Build · Deploy", sub: "merge", run: 0.64, done: 0.74 },
  { id: "approve", x: 1235, y: 280, icon: BadgeCheck, title: "PO Sign-off", sub: "approve", run: 0.76, done: 0.85 },
  { id: "ship", x: 1400, y: 280, icon: Rocket, title: "Ship", sub: "deploy", run: 0.85, done: 0.93 },
];

const EDGES: EdgeDef[] = [
  { from: "request", to: "po", a: 0.01, b: 0.08 },
  { from: "po", to: "planner", a: 0.12, b: 0.2 },
  { from: "po", to: "designer", a: 0.13, b: 0.21 },
  { from: "planner", to: "retriever", a: 0.24, b: 0.31 },
  { from: "planner", to: "fe1", a: 0.25, b: 0.32 },
  { from: "planner", to: "fe2", a: 0.25, b: 0.33 },
  { from: "designer", to: "be1", a: 0.26, b: 0.33 },
  { from: "designer", to: "be2", a: 0.27, b: 0.34 },
  { from: "retriever", to: "security", a: 0.42, b: 0.5 },
  { from: "fe1", to: "review", a: 0.43, b: 0.51 },
  { from: "fe2", to: "review", a: 0.44, b: 0.52 },
  { from: "be1", to: "qa", a: 0.43, b: 0.51 },
  { from: "be2", to: "qa", a: 0.44, b: 0.52 },
  { from: "qa", to: "be2", a: 0.55, b: 0.63, back: true },
  { from: "security", to: "deploy", a: 0.62, b: 0.7 },
  { from: "review", to: "deploy", a: 0.63, b: 0.71 },
  { from: "qa", to: "deploy", a: 0.64, b: 0.72 },
  { from: "deploy", to: "approve", a: 0.74, b: 0.82 },
  { from: "approve", to: "ship", a: 0.85, b: 0.92 },
];

const HW = 66; // node half-width
const HH = 23; // node half-height
const NODE_MAP = Object.fromEntries(NODES.map((n) => [n.id, n]));

const clamp = (v: number) => Math.max(0, Math.min(1, v));

const cable = (x1: number, y1: number, x2: number, y2: number) => {
  const dx = Math.max(40, Math.abs(x2 - x1) * 0.55);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
};

const backCable = (x1: number, y1: number, x2: number, y2: number) => {
  const dip = Math.max(y1, y2) + 75;
  return `M ${x1} ${y1} C ${x1} ${dip}, ${x2} ${dip}, ${x2} ${y2}`;
};

type NodeState = "idle" | "running" | "done";

const stateOf = (n: NodeDef, p: number): NodeState => (p >= n.done ? "done" : p >= n.run ? "running" : "idle");

const cardStyle = (state: NodeState) => ({
  borderColor: state === "running" ? "hsl(var(--accent))" : state === "done" ? "hsl(var(--primary) / 0.55)" : "hsl(var(--border))",
  boxShadow: state === "running" ? "0 0 18px -4px hsl(var(--accent) / 0.55)" : "none",
});

const iconBg = (state: NodeState) =>
  state === "running" ? "hsl(var(--accent) / 0.2)" : state === "done" ? "hsl(var(--primary) / 0.18)" : "hsl(var(--secondary))";

const LAUNCH_GREEN = "hsl(150 80% 45%)";

const NodeCard = ({ node, state }: { node: NodeDef; state: NodeState }) => {
  const Icon = node.icon;
  const running = state === "running";
  const done = state === "done";
  const success = node.id === "ship" && done; // Ship turns green on launch
  return (
    <foreignObject x={node.x - HW} y={node.y - HH} width={HW * 2} height={HH * 2} style={{ overflow: "visible" }}>
      <div
        className="relative flex h-[46px] items-center gap-1.5 rounded-lg border bg-background/85 px-2 backdrop-blur-sm transition-[border-color,box-shadow,transform] duration-500"
        style={{
          borderColor: success ? LAUNCH_GREEN : cardStyle(state).borderColor,
          boxShadow: success ? `0 0 22px -2px ${LAUNCH_GREEN}` : cardStyle(state).boxShadow,
          transform: running ? "translateY(-2px)" : "translateY(0)",
        }}
      >
        <span className="absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border" style={{ background: state === "idle" ? "hsl(var(--card))" : success ? LAUNCH_GREEN : "hsl(var(--accent))", borderColor: "hsl(var(--border))" }} />
        <span className="absolute -right-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border" style={{ background: done ? (success ? LAUNCH_GREEN : "hsl(var(--accent))") : "hsl(var(--card))", borderColor: "hsl(var(--border))" }} />
        <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-500" style={{ background: success ? "hsl(150 80% 45% / 0.18)" : iconBg(state) }}>
          {done ? <Check size={13} style={{ color: success ? LAUNCH_GREEN : "hsl(var(--accent))" }} /> : <Icon size={13} style={{ color: running ? "hsl(var(--accent))" : "hsl(var(--muted-foreground))" }} />}
          {running && <span className="absolute inset-0 rounded-md border border-accent/60 animate-ping" style={{ animationDuration: "1.4s" }} />}
        </span>
        <div className="min-w-0">
          <p className="truncate text-[11px] font-semibold leading-tight text-foreground">{node.title}</p>
          <p className="font-mono text-[8px] uppercase tracking-wider transition-colors duration-500" style={{ color: success ? LAUNCH_GREEN : running ? "hsl(var(--accent))" : done ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))" }}>
            {success ? "launched" : running ? "running…" : done ? "done" : node.sub}
          </p>
        </div>
      </div>
    </foreignObject>
  );
};

const ROCKET_PATH = "M 1400 256 C 1418 188, 1448 132, 1466 78";

const RocketLaunch = () => (
  <g style={{ pointerEvents: "none" }}>
    {/* trailing exhaust particles — lag behind the rocket using identical easing */}
    {[0.12, 0.24, 0.38].map((delay, i) => (
      <circle key={i} r={3.5 - i} fill="hsl(150 80% 55%)" filter="url(#pkt-glow)">
        <animateMotion dur="2.6s" begin={`${delay}s`} repeatCount="indefinite" path={ROCKET_PATH} keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines="0.5 0 0.9 0.4" />
        <animate attributeName="opacity" values="0;0.85;0" keyTimes="0;0.25;0.6" dur="2.6s" begin={`${delay}s`} repeatCount="indefinite" />
      </circle>
    ))}
    {/* rocket */}
    <g>
      <animateMotion dur="2.6s" repeatCount="indefinite" path={ROCKET_PATH} keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines="0.5 0 0.9 0.4" />
      <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.55;0.78" dur="2.6s" repeatCount="indefinite" />
      <text fontSize="26" textAnchor="middle" dominantBaseline="central" transform="rotate(-28)" style={{ filter: "drop-shadow(0 0 6px hsl(150 80% 50%))" }}>
        🚀
      </text>
    </g>
  </g>
);


export function AgentPipeline({ p }: { p: number }) {
  return (
    <svg viewBox="0 0 1480 600" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <filter id="pkt-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <marker id="back-arrow" markerWidth="7" markerHeight="7" refX="4" refY="3.5" orient="auto">
          <path d="M0 0 L7 3.5 L0 7 z" fill="hsl(var(--destructive))" />
        </marker>
      </defs>

      {EDGES.map((e, i) => {
        const s = NODE_MAP[e.from];
        const t = NODE_MAP[e.to];
        const d = e.back ? backCable(s.x, s.y + HH, t.x, t.y + HH) : cable(s.x + HW, s.y, t.x - HW, t.y);
        const fill = clamp((p - e.a) / (e.b - e.a));
        const active = fill > 0.02;
        const color = e.back ? "hsl(var(--destructive))" : "hsl(var(--accent))";
        return (
          <g key={`edge-${i}`}>
            <path d={d} fill="none" stroke="hsl(var(--border))" strokeWidth={1.6} strokeDasharray={e.back ? "5 5" : undefined} />
            <path
              d={d}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeDasharray={e.back ? "5 5" : 1}
              pathLength={e.back ? undefined : 1}
              strokeDashoffset={e.back ? undefined : 1 - fill}
              opacity={e.back ? fill : 1}
              markerEnd={e.back ? "url(#back-arrow)" : undefined}
              style={{ transition: "stroke-dashoffset 120ms linear, opacity 200ms ease", filter: `drop-shadow(0 0 4px ${color})` }}
            />
            <circle r={3.4} fill={color} filter="url(#pkt-glow)" opacity={active ? 1 : 0} style={{ transition: "opacity 300ms ease" }}>
              <animateMotion dur="1.7s" repeatCount="indefinite" path={d} keyPoints="0;1" keyTimes="0;1" calcMode="linear" />
            </circle>
          </g>
        );
      })}

      {NODES.map((n) => (
        <NodeCard key={n.id} node={n} state={stateOf(n, p)} />
      ))}

      {/* Rocket launch — fires once the Ship node is reached */}
      {p >= NODE_MAP.ship.done && <RocketLaunch />}
    </svg>
  );
}

/* ---------- Mobile vertical pipeline (organic, scroll-driven) ---------- */

const MHW = 76; // mobile node half-width
const MHH = 23; // mobile node half-height
const MVB_W = 390;
const MVB_H = 1180;

const M_POS: Record<string, { x: number; y: number }> = {
  request: { x: 195, y: 46 },
  po: { x: 195, y: 152 },
  planner: { x: 110, y: 266 },
  designer: { x: 280, y: 266 },
  retriever: { x: 195, y: 380 },
  fe1: { x: 110, y: 482 },
  fe2: { x: 280, y: 482 },
  be1: { x: 110, y: 588 },
  be2: { x: 280, y: 588 },
  security: { x: 110, y: 702 },
  review: { x: 280, y: 702 },
  qa: { x: 195, y: 808 },
  deploy: { x: 195, y: 916 },
  approve: { x: 195, y: 1022 },
  ship: { x: 195, y: 1128 },
};

const vCable = (x1: number, y1: number, x2: number, y2: number) => {
  const dy = (y2 - y1) * 0.5;
  const k = Math.sign(dy || 1) * Math.max(34, Math.abs(dy));
  return `M ${x1} ${y1} C ${x1} ${y1 + k}, ${x2} ${y2 - k}, ${x2} ${y2}`;
};

const M_ROCKET_PATH = "M 195 1105 C 214 1040, 168 975, 205 905";

const MobileRocketLaunch = () => (
  <g style={{ pointerEvents: "none" }}>
    {/* trailing exhaust particles */}
    {[0.12, 0.24, 0.38].map((delay, i) => (
      <circle key={i} r={3.5 - i} fill="hsl(150 80% 55%)" filter="url(#m-pkt-glow)">
        <animateMotion dur="2.6s" begin={`${delay}s`} repeatCount="indefinite" path={M_ROCKET_PATH} keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines="0.5 0 0.9 0.4" />
        <animate attributeName="opacity" values="0;0.85;0" keyTimes="0;0.25;0.6" dur="2.6s" begin={`${delay}s`} repeatCount="indefinite" />
      </circle>
    ))}
    {/* rocket */}
    <g>
      <animateMotion dur="2.6s" repeatCount="indefinite" path={M_ROCKET_PATH} keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines="0.5 0 0.9 0.4" />
      <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.55;0.78" dur="2.6s" repeatCount="indefinite" />
      <text fontSize="26" textAnchor="middle" dominantBaseline="central" transform="rotate(-28)" style={{ filter: "drop-shadow(0 0 6px hsl(150 80% 50%))" }}>
        🚀
      </text>
    </g>
  </g>
);

const MobileNodeCard = ({ node, state }: { node: NodeDef; state: NodeState }) => {
  const Icon = node.icon;
  const pos = M_POS[node.id];
  const running = state === "running";
  const done = state === "done";
  const success = node.id === "ship" && done;
  return (
    <foreignObject x={pos.x - MHW} y={pos.y - MHH} width={MHW * 2} height={MHH * 2} style={{ overflow: "visible" }}>
      <div
        className="relative flex h-[46px] items-center gap-2 rounded-xl border bg-background/85 px-2.5 backdrop-blur-sm transition-[border-color,box-shadow,transform] duration-500"
        style={{
          borderColor: success ? LAUNCH_GREEN : cardStyle(state).borderColor,
          boxShadow: success ? `0 0 22px -2px ${LAUNCH_GREEN}` : cardStyle(state).boxShadow,
        }}
      >
        <span className="absolute -top-[5px] left-1/2 h-2 w-2 -translate-x-1/2 rounded-full border" style={{ background: state === "idle" ? "hsl(var(--card))" : "hsl(var(--accent))", borderColor: "hsl(var(--border))" }} />
        <span className="absolute -bottom-[5px] left-1/2 h-2 w-2 -translate-x-1/2 rounded-full border" style={{ background: done ? (success ? LAUNCH_GREEN : "hsl(var(--accent))") : "hsl(var(--card))", borderColor: "hsl(var(--border))" }} />
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors duration-500" style={{ background: success ? "hsl(150 80% 45% / 0.18)" : iconBg(state) }}>
          {done ? <Check size={15} style={{ color: success ? LAUNCH_GREEN : "hsl(var(--accent))" }} /> : <Icon size={15} style={{ color: running ? "hsl(var(--accent))" : "hsl(var(--muted-foreground))" }} />}
          {running && <span className="absolute inset-0 rounded-md border border-accent/60 animate-ping" style={{ animationDuration: "1.4s" }} />}
        </span>
        <div className="min-w-0">
          <p className="truncate text-[12px] font-semibold leading-tight text-foreground">{node.title}</p>
          <p className="font-mono text-[8px] uppercase tracking-wider transition-colors duration-500" style={{ color: success ? LAUNCH_GREEN : running ? "hsl(var(--accent))" : done ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))" }}>
            {success ? "launched" : running ? "running…" : done ? "done" : node.sub}
          </p>
        </div>
      </div>
    </foreignObject>
  );
};

export function AgentPipelineMobile() {
  const ref = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      setP(1);
      return;
    }
    return subscribeScroll(() => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // The mobile pipeline is a tall element, so map progress to how far a
      // node has scrolled past a trigger line near the bottom of the
      // viewport. A node only activates once it is actually in view, instead
      // of completing based on the (far off-screen) element center.
      const triggerY = vh * 0.72;
      const p = (triggerY - rect.top) / rect.height;
      setP(Math.max(0, Math.min(1, p)));
    });
  }, []);

  return (
    <div ref={ref} className="mx-auto w-full max-w-[360px]" style={{ aspectRatio: `${MVB_W} / ${MVB_H}` }}>
      <svg viewBox={`0 0 ${MVB_W} ${MVB_H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="m-pkt-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <marker id="m-back-arrow" markerWidth="7" markerHeight="7" refX="4" refY="3.5" orient="auto">
            <path d="M0 0 L7 3.5 L0 7 z" fill="hsl(var(--destructive))" />
          </marker>
        </defs>

        {EDGES.map((e, i) => {
          const s = M_POS[e.from];
          const t = M_POS[e.to];
          const d = e.back ? vCable(s.x, s.y - MHH, t.x, t.y + MHH) : vCable(s.x, s.y + MHH, t.x, t.y - MHH);
          const fill = clamp((p - e.a) / (e.b - e.a));
          const active = fill > 0.02;
          const color = e.back ? "hsl(var(--destructive))" : "hsl(var(--accent))";
          return (
            <g key={`m-edge-${i}`}>
              <path d={d} fill="none" stroke="hsl(var(--border))" strokeWidth={1.6} strokeDasharray={e.back ? "5 5" : undefined} />
              <path
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeDasharray={e.back ? "5 5" : 1}
                pathLength={e.back ? undefined : 1}
                strokeDashoffset={e.back ? undefined : 1 - fill}
                opacity={e.back ? fill : 1}
                markerEnd={e.back ? "url(#m-back-arrow)" : undefined}
                style={{ transition: "stroke-dashoffset 120ms linear, opacity 200ms ease", filter: `drop-shadow(0 0 4px ${color})` }}
              />
              <circle r={3.4} fill={color} filter="url(#m-pkt-glow)" opacity={active ? 1 : 0} style={{ transition: "opacity 300ms ease" }}>
                <animateMotion dur="1.7s" repeatCount="indefinite" path={d} keyPoints="0;1" keyTimes="0;1" calcMode="linear" />
              </circle>
            </g>
          );
        })}

        {NODES.map((n) => (
          <MobileNodeCard key={n.id} node={n} state={stateOf(n, p)} />
        ))}

        {/* Rocket launch — fires once the Ship node is reached */}
        {p >= NODE_MAP.ship.done && <MobileRocketLaunch />}
      </svg>
    </div>
  );
}
